import React from 'react';
import TripDayItem from '../TripDayItem';
import TripDayItemSelector from '../TripDayItemSelector';
import TripDayItemSelectorDisabled from '../TripDayItemSelectorDisabled';
import TripDayExperienceCard from './TripDayExperienceCard';
import TripDayExperienceRemoveConfirmationModal from './TripDayExperienceRemoveConfirmationModal';
import { useTripDayExperienceState } from './useTripDayExperienceState';
import type { NotifyFn, RenderAccessAlert, Translate } from './types';

interface TripDayExperienceProps {
  dayIndex: number;
  date?: any;
  addExperience: (experienceId: string) => void;
  // KD-3678 — the day's itinerary ("Hanoi → Hanoi"), already formatted by the
  // day card: it reads as a qualifier of the program name, so it is rendered
  // right under it rather than as a row of its own above the card.
  itineraryLabel?: string;
  // The day belongs to another organisation (`useIsDayEditable`, forwarded by
  // TripDay): it shows what it holds and offers no way to change it.
  readOnly?: boolean;
  // KD-3581 — the frame this day belongs to lends three things: the DOM slot
  // that closes it (where the global services band goes, AC03), the registry
  // its header removal cross calls into, and the undo it snapshots into (AC09).
  globalServicesSlot?: any;
  registerRemoveHandler?: (...args: any[]) => void;
  onExperienceRemoved?: (info: any) => void;
  registerExperienceWarning?: (...args: any[]) => void;
  translation: Translate;
  notify?: NotifyFn;
  onEvent?: (name: string, payload: unknown) => void;
  // The corner "cannot be booked" warning. Absent for an app that has no such
  // warning to show.
  renderAccessAlert?: RenderAccessAlert;
  // The additional-services modal is still a large, per-app component (not
  // moved by this PRD) — rendered through this slot with every piece of data
  // it needs already computed.
  renderExtrasModal: (props: Record<string, any>) => React.ReactNode;
}

export default function TripDayExperience({
  dayIndex,
  date,
  addExperience,
  itineraryLabel,
  readOnly = false,
  globalServicesSlot,
  registerRemoveHandler,
  onExperienceRemoved,
  registerExperienceWarning,
  translation: t,
  notify,
  onEvent,
  renderAccessAlert,
  renderExtrasModal,
}: TripDayExperienceProps) {
  const {
    experience,
    pricing,
    services,
    removal,
    canDuplicateExperience,
    extrasModalProps,
    openDayOptions,
    openGlobalOptions,
  } = useTripDayExperienceState({
    dayIndex,
    date,
    readOnly,
    registerRemoveHandler,
    onExperienceRemoved,
    registerExperienceWarning,
    t,
    notify,
    onEvent,
  });

  if (!experience && readOnly) {
    return (
      <div className="h-full">
        <TripDayItemSelectorDisabled>
          {t('compose.managedByPartner')}
        </TripDayItemSelectorDisabled>
      </div>
    );
  }

  if (!experience) {
    return (
      <div className="h-full">
        <TripDayItemSelector
          dayIndex={dayIndex}
          type="experience"
          addProgramLabel={t('compose.addProgram')}
          addHotelLabel={t('compose.addHotel')}
          exploreIncludedHotelsLabel={t('compose.exploreIncludedHotels')}
          exploreSuggestedHotelsLabel={t('compose.exploreSuggestedHotels')}
          noProgramLabel={t('compose.dayCard.noProgram')}
          orLabel={t('compose.or')}
          bookOnMyOwnLabel={t('compose.bookOnMyOwn')}
        />
      </div>
    );
  }

  return (
    <div className="h-full">
      <TripDayItem>
        <TripDayExperienceCard
          experience={experience}
          itineraryLabel={itineraryLabel}
          canDuplicateExperience={canDuplicateExperience}
          onDuplicate={() => addExperience(experience.id)}
          priceLoading={pricing.priceLoading}
          hasAvailabilityWarning={pricing.hasAvailabilityWarning}
          requiredMinPax={pricing.requiredMinPax}
          isCurrencyMismatch={pricing.isCurrencyMismatch}
          renderAccessAlert={renderAccessAlert}
          dayExtrasCount={services.dayExtrasCount}
          dayAvailableCount={pricing.dayAvailableCount}
          daySelectedExtras={services.daySelectedExtras}
          globalExtrasCount={services.globalExtrasCount}
          globalAvailableCount={services.globalAvailableCount}
          globalSelectedExtras={services.globalSelectedExtras}
          sumExtrasPrice={services.sumExtrasPrice}
          extrasCurrency={services.extrasCurrency}
          extrasAmountPerPax={services.extrasAmountPerPax}
          renderServiceChips={services.renderServiceChips}
          onOpenDayOptions={openDayOptions}
          onOpenGlobalOptions={openGlobalOptions}
          globalServicesSlot={globalServicesSlot}
          t={t}
        />

        {renderExtrasModal(extrasModalProps)}

        {removal.showRemoveConfirmation && (
          <TripDayExperienceRemoveConfirmationModal
            open={removal.showRemoveConfirmation}
            setOpen={removal.setShowRemoveConfirmation}
            onConfirm={removal.confirmRemoveExperience}
            experience={experience}
            previousExperience={removal.previousExperience}
            requiredExtras={removal.requiredExtras}
            t={t}
          />
        )}
      </TripDayItem>
    </div>
  );
}
